import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { GoogleAPIProvider } from "@jti/fe-clients";
import { FlotiqApi } from "@jti/rest-api";
import { PrizesList } from "../../components/PrizeLocator/components";
import { useConfig } from "../../providers/useConfig";
import { useLocale } from "../../providers/useLocale";
import { CategoryFilter } from "./components/CategoryFilter";
import { PrizeLocatorMap } from "./components/PrizeLocatorMap";
import { getLoaderOptions } from "./components/PrizeLocatorMap/prize-locator.config";
import { PrizeLocatorSearchBar } from "./components/PrizeLocatorSearchBar";
import { getActiveItemIndex } from "./utils/getActiveItemIndex";
import { PrizeLocatorContext } from "./hooks";
import { calculateRadius, getFiltersBaseOnFlotiqResponse, getStoresFilteredBySelectedFilters, getTransformedMarkers, } from "./utils";
import styles from "./PrizeLocator.module.scss";
export const GOOGLE_DIRECTION_URL_CONST = "https://www.google.com/maps/dir/?api=1&destination=";
export const PrizeLocator = ({ defaultLat, defaultLng, flotiqApiKey, flotiqContentType, filtersTranslations, activeStoreId = "", context, }) => {
    const [map, setMap] = useState();
    const [currentStoreId, setCurrentStoreId] = useState(activeStoreId);
    const [userMarkerPosition, setUserMarkerPosition] = useState();
    const [displayType, setDisplayType] = useState("map");
    const [stores, setStores] = useState([]);
    const [filteredStores, setFilteredStores] = useState([]);
    const [filters, setFilters] = useState([]);
    const [activeFilters, setActiveFilters] = useState([]);
    const { FLOTIQ_API_URL } = useConfig();
    const flotiqApi = new FlotiqApi(FLOTIQ_API_URL, flotiqApiKey, flotiqContentType);
    const config = useConfig();
    const locale = useLocale();
    const isFiltersAvailable = filters.length > 0;
    const filterStores = useCallback(() => {
        const storesFilteredBySelectedFilters = isFiltersAvailable
            ? getStoresFilteredBySelectedFilters(stores, activeFilters)
            : stores;
        setFilteredStores(storesFilteredBySelectedFilters);
    }, [stores, isFiltersAvailable, activeFilters]);
    const loadMarkersFromApi = async (map) => {
        const center = map.getCenter();
        const radius = calculateRadius(map);
        if (!center || !radius) {
            return;
        }
        try {
            const response = await flotiqApi.getStores(radius, center.lat(), center.lng());
            if (!response.data) {
                return;
            }
            const transformedMarkers = getTransformedMarkers(response.data);
            if (filtersTranslations) {
                const filtersBaseOnResult = getFiltersBaseOnFlotiqResponse(transformedMarkers, filtersTranslations);
                setFilters(filtersBaseOnResult);
            }
            setStores(transformedMarkers);
        }
        catch (e) {
            console.error(e, "error");
        }
    };
    const debouncedLoadMarkersFromApi = useCallback(debounce(loadMarkersFromApi, 300), []);
    const onSelectStore = (storeId) => {
        setCurrentStoreId(storeId);
    };
    const onChangeResultType = (type) => {
        setDisplayType(stores.length > 0 ? type : "map");
    };
    const handleFiltersChange = (key) => {
        const activeItemIndex = getActiveItemIndex(key, activeFilters);
        if (activeItemIndex !== -1) {
            setActiveFilters((prevState) => prevState.toSpliced(activeItemIndex, 1));
        }
        else {
            const activeItem = filters.find((filter) => filter.categoryKey === key);
            setActiveFilters((prevState) => [...prevState, activeItem]);
        }
    };
    useEffect(() => {
        filterStores();
    }, [filterStores]);
    return (_jsx(PrizeLocatorContext.Provider, { value: context, children: _jsx(GoogleAPIProvider, { apiKey: config.GOOGLE_MAPS_API, options: getLoaderOptions(config, locale), children: _jsxs("div", { className: classNames(styles.prizeLocator, "light-theme"), children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsx(PrizeLocatorSearchBar, { onChangeResultType: onChangeResultType, count: filteredStores.length, map: map, onLoadUserLocationMarker: setUserMarkerPosition, loadMarkersFromApi: debouncedLoadMarkersFromApi }), _jsx(CategoryFilter, { onFilterClick: handleFiltersChange, activeFilters: activeFilters, filters: filters })] }), _jsxs("div", { className: styles.resultsWrapper, children: [_jsx(PrizesList, { activeStoreId: currentStoreId, stores: filteredStores, onStoreSelect: onSelectStore, displayType: displayType }), _jsx("div", { className: classNames(styles.mapContainer, {
                                    [styles.mapContainerHidden]: displayType !== "map",
                                }), children: _jsx(PrizeLocatorMap, { defaultLat: defaultLat, activeStoreId: currentStoreId, defaultLng: defaultLng, loadMarkersFromApi: debouncedLoadMarkersFromApi, onMapLoaded: setMap, onSelectStore: onSelectStore, userMarkerPosition: userMarkerPosition, stores: filteredStores, flotiqApi: flotiqApi, setStores: setStores }) })] })] }) }) }));
};
//# sourceMappingURL=PrizeLocator.js.map